import { startRouter, type Route } from './router.js';
import { renderLogin } from './pages/Login.js';
import { renderLanding } from './pages/Landing.js';
import { renderRegister } from './pages/Register.js';
import { renderForgotPassword } from './pages/ForgotPassword.js';
import { renderResetPassword } from './pages/ResetPassword.js';
import { renderVerifyEmail } from './pages/VerifyEmail.js';
import { renderTrainingYard } from './pages/TrainingYard.js';
import { renderMisdeeds } from './pages/Misdeeds.js';
import { renderGoodDeeds } from './pages/GoodDeeds.js';
import { renderInventory } from './pages/Inventory.js';
import { renderInfirmary } from './pages/Infirmary.js';
import { renderDungeon } from './pages/Dungeon.js';
import { renderScholarium } from './pages/Scholarium.js';
import { renderTrades } from './pages/Trades.js';
import { renderForge, renderApothecary, renderCookfire, renderBrewhouse } from './pages/crafting.js';
import { renderReplays } from './pages/Replays.js';
import { renderMarket } from './pages/Market.js';
import { renderTravel } from './pages/Travel.js';
import { renderDomiciles } from './pages/Domiciles.js';
import { renderSparring } from './pages/Sparring.js';
import { renderAttacks } from './pages/Attacks.js';
import { renderHouses } from './pages/Houses.js';
import { renderAwards } from './pages/Awards.js';
import { renderExpeditions } from './pages/Expeditions.js';
import { renderRacing } from './pages/Racing.js';
import { renderCrypt } from './pages/Crypt.js';
import { renderShadesDue } from './pages/ShadesDue.js';
import { renderBanks } from './pages/Banks.js';
import { renderMail } from './pages/Mail.js';
import { renderSettings } from './pages/Settings.js';
import { renderLeaderboards } from './pages/Leaderboards.js';
import { renderFriends } from './pages/Friends.js';
import { renderDailyTasks } from './pages/DailyTasks.js';
import { renderEvents } from './pages/Events.js';
import { renderShoutbox } from './pages/Shoutbox.js';
import { renderPatchNotes } from './pages/PatchNotes.js';
import { renderRules } from './pages/Rules.js';
import { renderTos } from './pages/Tos.js';
import { renderPrivacy } from './pages/Privacy.js';
import { renderProfile } from './pages/Profile.js';
import { renderWiki } from './pages/Wiki.js';
import { renderPublicProfile } from './pages/PublicProfile.js';
import { renderPublicHouse } from './pages/PublicHouse.js';
import { renderMembership } from './pages/Membership.js';
import { api } from './api/client.js';
import { store } from './state/store.js';
import { hasCompletedTour, showWelcomeTour } from './components/WelcomeTour.js';
import { mountInteractiveTutorial } from './components/InteractiveTutorial.js';
import { mountTermsAcceptanceGate } from './components/TermsAcceptanceGate.js';
import { mountLevelUpWatcher } from './components/LevelUpModal.js';
import { mountShadeWatcher } from './components/ShadeRaidModal.js';
import { mountAwardWatcher } from './components/AwardModal.js';
import { mountComplineBanner } from './components/ComplineBanner.js';
import { mountPresenceHeartbeat } from './util/presenceHeartbeat.js';
import { initTheme } from './state/theme.js';

async function main(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) throw new Error('Missing #app root element');

  // Apply the user's theme preference before the first paint so we don't
  // flash light-mode into dark-mode (or vice versa).
  initTheme();

  // Try to recover an existing session before the first render.
  // This calls /auth/refresh against the httpOnly cookie and then /me.
  try {
    // Never let a slow or stuck startup call block the first paint. If the
    // session probe (refresh + /me) doesn't resolve within a few seconds,
    // render anyway as logged-out; the session recovers on the next request,
    // or a real outage surfaces through the down/maintenance overlay. Without
    // this race, a hung startup request leaves the whole app blank.
    const me = await Promise.race([
      api.bootstrapSession(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 7000)),
    ]);
    if (me) {
      store.set({
        // accessToken was stored inside bootstrapSession via tryRefresh
        account: me.account,
        character: me.character,
      });
    }
  } catch {
    // ignore — we'll just route to /login
  }

  const routes: Route[] = [
    // The front gate — logged-out visitors get the pitch, not a password field.
    // (Signed-in visitors are redirected to /profile by the router.)
    { path: '/', requireAuth: false, render: renderLanding },
    { path: '/login', requireAuth: false, render: renderLogin },
    { path: '/register', requireAuth: false, render: renderRegister },
    { path: '/forgot-password', requireAuth: false, render: renderForgotPassword },
    { path: '/reset-password', requireAuth: false, render: renderResetPassword },
    { path: '/verify-email', requireAuth: false, render: renderVerifyEmail },
    { path: '/training', requireAuth: true, render: renderTrainingYard },
    { path: '/sparring', requireAuth: true, render: renderSparring },
    { path: '/attacks', requireAuth: true, render: renderAttacks },
    { path: '/misdeeds', requireAuth: true, render: renderMisdeeds },
    { path: '/good-deeds', requireAuth: true, render: renderGoodDeeds },
    { path: '/scholarium', requireAuth: true, render: renderScholarium },
    { path: '/trades', requireAuth: true, render: renderTrades },
    { path: '/forge', requireAuth: true, render: renderForge },
    { path: '/apothecary', requireAuth: true, render: renderApothecary },
    { path: '/cookfire', requireAuth: true, render: renderCookfire },
    { path: '/brewhouse', requireAuth: true, render: renderBrewhouse },
    { path: '/replays', requireAuth: true, render: renderReplays },
    { path: '/market', requireAuth: true, render: renderMarket },
    { path: '/travel', requireAuth: true, render: renderTravel },
    { path: '/domiciles', requireAuth: true, render: renderDomiciles },
    { path: '/houses', requireAuth: true, render: renderHouses },
    { path: '/awards', requireAuth: true, render: renderAwards },
    { path: '/expeditions', requireAuth: true, render: renderExpeditions },
    { path: '/racing', requireAuth: true, render: renderRacing },
    { path: '/crypt', requireAuth: true, render: renderCrypt },
    { path: '/shades-due', requireAuth: true, render: renderShadesDue },
    { path: '/banks', requireAuth: true, render: renderBanks },
    { path: '/mail', requireAuth: true, render: renderMail },
    // Deep-link to a specific letter (e.g. from a "new mail" notification).
    // renderMail reads the id from the hash and preselects that letter.
    { path: '/mail/:id', requireAuth: true, render: renderMail },
    { path: '/leaderboards', requireAuth: true, render: renderLeaderboards },
    { path: '/friends', requireAuth: true, render: renderFriends },
    { path: '/daily', requireAuth: true, render: renderDailyTasks },
    { path: '/events', requireAuth: true, render: renderEvents },
    { path: '/shoutbox', requireAuth: true, render: renderShoutbox },
    { path: '/patch-notes', requireAuth: true, render: renderPatchNotes },
    { path: '/wiki', requireAuth: true, render: renderWiki },
    // Legal / governance pages — readable without auth so unregistered
    // visitors can review them before signing up.
    { path: '/rules', requireAuth: false, render: renderRules },
    { path: '/tos', requireAuth: false, render: renderTos },
    { path: '/privacy', requireAuth: false, render: renderPrivacy },
    { path: '/inventory', requireAuth: true, render: renderInventory },
    { path: '/infirmary', requireAuth: true, render: renderInfirmary },
    { path: '/dungeon', requireAuth: true, render: renderDungeon },
    { path: '/profile', requireAuth: true, render: renderProfile },
    { path: '/u/:id', requireAuth: true, render: renderPublicProfile },
    { path: '/house/:id', requireAuth: true, render: renderPublicHouse },
    { path: '/membership', requireAuth: true, render: renderMembership },
    { path: '/settings', requireAuth: true, render: renderSettings },
  ];

  startRouter(root, routes);

  // Watch for level-ups across the whole session and pop a celebration modal.
  // Subscribes to the store once; seeds from current state without firing, so
  // it only celebrates a genuine level increase (training, deeds, expeditions).
  mountLevelUpWatcher();

  // The Shade — polls the player's dark-raid status and pops a blocking herald
  // the moment it fixes on them, routing them to the Hall of Combat.
  mountShadeWatcher();

  // Pop any individually-granted award the moment it's waiting, wherever the
  // player is — they click Accept to claim it.
  mountAwardWatcher();

  // The Compline bell — raises a header banner during the evening blessing
  // window (8pm Sydney) and self-manages on a timer.
  mountComplineBanner();

  // Activity heartbeat — marks the player "online" only while the tab is visible
  // and they've interacted recently, so a left-open/idle tab no longer shows
  // online forever.
  mountPresenceHeartbeat();

  // First-login welcome tour. Fires once per account-on-this-browser — but
  // never over the verify-email / reset-password flows, where it buried the
  // confirmation the player actually came to see. On a suppressed route the
  // tour simply waits for the next navigation.
  const TOUR_SUPPRESSED_ROUTES = new Set(['/verify-email', '/reset-password']);
  let tourShown = false;
  const maybeShowWelcomeTour = (): void => {
    if (tourShown) return;
    if (!store.get().account || hasCompletedTour()) return;
    const path = (window.location.hash || '#/').slice(1).split('?')[0] || '/';
    if (TOUR_SUPPRESSED_ROUTES.has(path)) return;
    tourShown = true;
    // Wait a tick so the shell mounts before the modal overlays on top.
    setTimeout(() => { void showWelcomeTour(); }, 600);
  };
  maybeShowWelcomeTour();

  // Re-mount the interactive tutorial + terms gate after every route change.
  // Each component is a no-op when its trigger condition is unmet.
  window.addEventListener('hashchange', () => {
    if (store.get().account) {
      maybeShowWelcomeTour();
      mountInteractiveTutorial();
      mountTermsAcceptanceGate();
    }
  });
  if (store.get().account) {
    mountInteractiveTutorial();
    mountTermsAcceptanceGate();
  }

  // Re-sync from the server when the tab regains focus. Timers (incl. the
  // resource-bar ticker) are throttled/frozen while a tab is backgrounded, so
  // on return we pull a fresh /me to reset the bar baseline + timestamp to the
  // true server values. The bars then tick forward from that fresh anchor.
  // Throttled so rapid focus flips don't spam the API.
  let lastFocusSync = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!store.get().accessToken) return;
    if (Date.now() - lastFocusSync < 5000) return;
    lastFocusSync = Date.now();
    void api.me()
      .then((me) => store.set({ account: me.account, character: me.character }))
      .catch(() => { /* offline / transient — overlay handles real outages */ });
  });
}

void main();
